import React, {useContext, useEffect, useState} from "react";
import {Link} from "react-router-dom";
import AddIcon from "@material-ui/icons/Add";
import {
    Box,
    Button,
    Container,
    FormControl,
    Grid,
    IconButton,
    Input,
    InputAdornment,
    Typography
} from '@deere/fuel-react';
import CriteriaFilter from "../shared/filter/CriteriaFilter";
import CriteriaList from "./CriteriaList";
import {Criteria} from "./Criteria";
import {
    checkedData,
    filterFunctionalAreas,
    searchWithDescList,
    sortAlphaNum,
    toCapitalize
} from "../shared/utils";
import {useAuthConfig} from "../shared/helper";
import SearchIcon from "@material-ui/icons/Search";
import "../assets/ListItem.scss";
import FunctionalAreaFilter from "../shared/filter/FunctionalAreaFilter";
import {UploadCriteriaDataModal} from "../shared/modal/UploadCriteriaDataModal";
import {UserContext} from "./Main";
import {useOktaAuth} from "@okta/okta-react";

export const CriteriaCatalog = ({name}) => {
    let authAxios = useAuthConfig();
    const {authState} = useOktaAuth();
    const info = useContext(UserContext);
    const isCriteriaAdmin = info && info.isCriteriaAdmin;
    const [criteriaList, setCriteriaList] = useState(null);
    const [query, setQuery] = useState("");
    const [searchParam] = useState(["criteria", "status"]);
    const [searchParamDesc] = useState(["category", "subCategory"]);
    const [statusData, setStatusData] = useState([]);
    const [statusFilterData, setStatusFilterData] = useState([]);
    const [statusExpanded, setStatusExpanded] = useState(true);
    const [categoryData, setCategoryData] = useState([]);
    const [categoryFilterData, setCategoryFilterData] = useState([]);
    const [categoryExpanded, setCategoryExpanded] = useState(false);
    const [subCategoryData, setSubCategoryData] = useState([]);
    const [subCategoryFilterData, setSubCategoryFilterData] = useState([]);
    const [subCategoryExpanded, setSubCategoryExpanded] = useState(false);
    const [categoriesList, setCategoriesList] = useState([]);
    const [functionalAreaData, setFunctionalAreaData] = useState([]);
    const [functionalAreaFilterData, setFunctionalAreaFilterData] = useState([]);
    const [functionalAreaExpanded, setFunctionalAreaExpanded] = useState(false);

    const getCriteriaComponent = (list) => {
        return list.map((item) => {
            return (
                <Criteria criteria={item} key={item.id} isCriteriaAdmin={isCriteriaAdmin}/>
            );
        });
    }

    const toggleStatusFilter = () => {
        setStatusExpanded(!statusExpanded);
    };

    const toggleCategoryFilter = () => {
        setCategoryExpanded(!categoryExpanded);
    };

    const toggleSubCategoryFilter = () => {
        setSubCategoryExpanded(!subCategoryExpanded);
    };

    const resetFilter = () => {
        setStatusFilterData(statusData)
        setCategoryFilterData(categoryData)
        setSubCategoryFilterData(subCategoryData)
        setFunctionalAreaFilterData(functionalAreaData)
    }

    const handleUpdatedStatus = data => {
        const arr = checkedData(data)
        setStatusFilterData(arr);
    }

    const handleUpdatedCategory = data => {
        const arr = checkedData(data)
        const filteredCategories = categoriesList.filter(item => arr.includes(item.description))
        const subcategoriesData = filteredCategories.map(item => {
            return item.subCategories.map((item) => {
                return item.description
            })
        })
        const filterData = subCategoryData.filter(item => subcategoriesData.flat(1).concat(["Not Selected"]).includes(item))
        setCategoryFilterData(arr);
        const list = subCategoryData.map(item => {
            return filterData.includes(item) ? {
                name: item,
                label: item && toCapitalize(item),
                checked: true
            } : {name: item, label: item && toCapitalize(item), checked: false}
        })
        sessionStorage.setItem("selectedSubcategory", JSON.stringify(list));
        setSubCategoryFilterData(filterData);
    }

    const handleUpdatedSubCategory = data => {
        const arr = checkedData(data)
        setSubCategoryFilterData(arr);
        const filteredCategories = categoriesList.filter(item => {
            const currentSubCat = item.subCategories.map(ai => ai.description)
            return arr.some(item => currentSubCat.includes(item));
        })
        const list = categoryData.map(item => {
            return filteredCategories.map(cat => cat.description).includes(item) ? {
                name: item,
                label: item && toCapitalize(item),
                checked: true
            } : {name: item, label: item && toCapitalize(item), checked: false}
        })
        sessionStorage.setItem("selectedCategory", JSON.stringify(list));
        setCategoryFilterData(filteredCategories.map(ai => ai.description));
    }

    const toggleFunctionalAreaFilter = () => {
        setFunctionalAreaExpanded(!functionalAreaExpanded);
    };

    const handleUpdatedFunctionalArea = data => {
        const arr = checkedData(data)
        setFunctionalAreaFilterData(arr);
    }

    const filterCriteriaList = (items) => {
        const sortedItems = items && items.sort((a, b) => {
            let descA = a.props.criteria.criteria.toLowerCase(),
                descB = b.props.criteria.criteria.toLowerCase();
            return sortAlphaNum(descA, descB)
        })
        return sortedItems && sortedItems.filter((item) => {
            const tempFunctionalArea = filterFunctionalAreas(item.props.criteria.functionalAreas, functionalAreaFilterData)
            return statusFilterData.includes(item.props.criteria.status)
                && categoryFilterData.includes(item.props.criteria.category.description)
                && subCategoryFilterData.includes(item.props.criteria.subCategory && item.props.criteria.subCategory.description)
                && searchWithDescList(item.props.criteria, searchParam, searchParamDesc, query)
                && tempFunctionalArea.length > 0
        })
    }

    const getFilterData = (sessionName, filterData) => {
        const statusData = sessionStorage[`${sessionName}`] ? JSON.parse(sessionStorage.getItem(`${sessionName}`)) : filterData;
        return checkedData(statusData)
    }

    useEffect(() => {
        const getCategories = async () => {
            try {
                return await authAxios.get(`/categories`);
            } catch (e) {
                console.error(e);
            }
        };

        getCategories().then(res => {
            setCategoriesList(res.data);
        })
        const getCriteriaCatalog = async () => {
            try {
                return await authAxios.get(`/criteria`);
            } catch (e) {
                console.error(e);
            }
        };
        const processCriteriaComponent = (response) => {
            const updatedResponse = response.map((row) => {
                if (row.subCategory === null) {
                    row.subCategory = {id: -1, description: "Not Selected"}
                }
                return row;
            });
            const uniqueStatus = [...new Set(updatedResponse.map(item => item.status))];
            const uniqueCategories = [...new Set(updatedResponse.map(item => item.category.description))];
            const uniqueSubCategories = [...new Set(updatedResponse.map(item => item.subCategory && item.subCategory.description))];
            const functionalAreas = updatedResponse.map(item => {
                return (item.functionalAreas.map(functionalArea => functionalArea.description))
            });
            const uniqueFunctionalAreas = ([...new Set([].concat(...functionalAreas))])

            setStatusData(uniqueStatus)
            const statusArray = getFilterData("selectedStatus", statusFilterData)
            setStatusFilterData(statusArray.length > 0 ? statusArray : uniqueStatus)
            setCategoryData(uniqueCategories)
            const categoryArray = getFilterData("selectedCategory", categoryFilterData)
            setCategoryFilterData(categoryArray.length > 0 ? categoryArray : uniqueCategories)
            setSubCategoryData(uniqueSubCategories)
            const subcategoryArray = getFilterData("selectedSubcategory", subCategoryFilterData)
            setSubCategoryFilterData(subcategoryArray.length > 0 ? subcategoryArray : uniqueSubCategories)
            setFunctionalAreaData(uniqueFunctionalAreas)
            const functionalAreaArray = getFilterData("selectedFunctionalAreas", functionalAreaFilterData)
            setFunctionalAreaFilterData(functionalAreaArray.length > 0 ? functionalAreaArray : uniqueFunctionalAreas)
            const list = getCriteriaComponent(response);
            setCriteriaList(list);
        }
        getCriteriaCatalog().then(res => {
            if (res)
                processCriteriaComponent(res.data)
        });

        return () => {
            setCriteriaList(null);
        } // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState]);

    return (
        <React.Fragment>
            <Grid container>
                <Grid item xs={12} md={3} className="sticky-header">
                    <Typography variant="h1" component="h1" data-testid="PageName">
                        {name}
                    </Typography>
                </Grid>
                <Grid item xs={12} md={9} className="sticky-header">
                    <Container>
                        <Box display="flex" justifyContent="flex-end">
                            {isCriteriaAdmin && (
                                <>
                                    <Box mr={2}>
                                        <UploadCriteriaDataModal/>
                                    </Box>
                                    <Link
                                        to={{
                                            pathname: "/criteria-catalog/create-criteria",
                                        }}
                                        style={{textDecoration: "none"}}
                                    >
                                        <Button variant="primary" startIcon={<AddIcon/>}>
                                            Create New
                                        </Button>
                                    </Link>
                                </>
                            )}
                        </Box>
                    </Container>
                </Grid>
            </Grid>
            <Grid container>
                <Grid item xs={12} md={3}>
                    <Box mt={4}>
                        <Button variant="text-inline" size="large" onClick={resetFilter}>Clear All</Button>
                    </Box>
                    <Box mt={1}>
                        <div className="jdsn-facet-container">
                            <CriteriaFilter
                                statusData={statusData}
                                filteredData={statusFilterData}
                                onExpandClick={toggleStatusFilter}
                                onStatusUpdate={handleUpdatedStatus}
                                expanded={statusExpanded}
                                name={"Criteria Status"}/>
                            <CriteriaFilter
                                statusData={categoryData}
                                filteredData={categoryFilterData}
                                onExpandClick={toggleCategoryFilter}
                                onStatusUpdate={handleUpdatedCategory}
                                expanded={categoryExpanded}
                                name={"Category"}/>
                            <CriteriaFilter
                                statusData={subCategoryData}
                                filteredData={subCategoryFilterData}
                                onExpandClick={toggleSubCategoryFilter}
                                onStatusUpdate={handleUpdatedSubCategory}
                                expanded={subCategoryExpanded}
                                name={"SubCategory"}/>
                            <FunctionalAreaFilter
                                statusData={functionalAreaData}
                                filteredData={functionalAreaFilterData}
                                onExpandClick={toggleFunctionalAreaFilter}
                                onStatusUpdate={handleUpdatedFunctionalArea}
                                expanded={functionalAreaExpanded}
                                name={"Functional Area"}/>

                            <Box className="fixed-bottom position-sticky view-result">
                                <Button variant="primary" fullWidth={true} size="large">View Results</Button>
                            </Box>
                        </div>
                    </Box>
                </Grid>
                <Grid item xs={12} md={9}>
                    <Container>
                        <Box mt={4}>
                            <FormControl fullWidth>
                                <Input fullWidth placeholder="Filter these results" id="search"
                                       endAdornment={
                                           <InputAdornment position="end">
                                               <IconButton aria-label="search">
                                                   <SearchIcon style={{color: " #367c2b"}}/>
                                               </IconButton>
                                           </InputAdornment>
                                       }
                                       onChange={(e) => setQuery(e.target.value)}/>
                            </FormControl>
                        </Box>
                        <Box mt={4}>
                            <CriteriaList list={filterCriteriaList(criteriaList)}/>
                        </Box>
                    </Container>
                </Grid>
            </Grid>
        </React.Fragment>
    );
}
